/**
 * Pure device logic for the call's mic / speaker / camera menu (GH-1859).
 *
 * Everything here is a function of device lists and the current selection, so
 * the auto-switch and fallback rules can be proven without real hardware —
 * `hooks/useCallDevices.ts` adds the `devicechange` listener, timers and the
 * actual track switching on top.
 *
 * A selection of `null` means "the OS default". Chrome exposes that as the
 * `default` pseudo-device; other browsers have no such entry, so a switch to
 * the default resolves to the first real device of that kind instead
 * (`resolveDeviceId`).
 */

export type AudioKind = "audioinput" | "audiooutput";
export type CallDeviceKind = AudioKind | "videoinput";

/** The subset of `MediaDeviceInfo` the planner reads — plain objects in tests. */
export interface CallDevice {
  deviceId: string;
  kind: CallDeviceKind;
  label: string;
  groupId: string;
}

export type DeviceSelection = Record<CallDeviceKind, string | null>;

export const OS_DEFAULT_SELECTION: DeviceSelection = {
  audioinput: null,
  audiooutput: null,
  videoinput: null,
};

/** Pre-join screens only list new devices; the call itself reacts to them. */
export type CallDevicePhase = "prejoin" | "incall";

export interface DeviceSwitch {
  kind: CallDeviceKind;
  deviceId: string;
}

export type DeviceNotice =
  /** "Now using <deviceName> · Undo" */
  | { kind: "auto"; deviceName: string }
  /** "Switched to <deviceName> — <oldName> disconnected" (no Undo) */
  | { kind: "fallback"; deviceName: string; oldName: string };

export interface DeviceChangePlan {
  selection: DeviceSelection;
  /** Track switches to perform, with ids already resolved. */
  switches: DeviceSwitch[];
  notice: DeviceNotice | null;
  /** Selection to restore on Undo — set only for an auto-switch. */
  undo: DeviceSelection | null;
}

const PSEUDO_IDS = new Set(["default", "communications"]);
const HEADSET_RE =
  /airpods|bluetooth|headset|headphone|earbuds|buds|hands-free|usb/i;
const USB_ID_RE = /\([0-9a-f]{4}:[0-9a-f]{4}\)/i;
const BUILT_IN_RE = /built-in|internal|integrated/i;
const PSEUDO_PREFIX_RE = /^(default|communications)\s*-\s*/i;
const AUDIO_KINDS: AudioKind[] = ["audioinput", "audiooutput"];

/** Chrome's "Default" / "Communications" entries mirror a real device. */
export function isPseudoDevice(device: Pick<CallDevice, "deviceId">): boolean {
  return PSEUDO_IDS.has(device.deviceId);
}

/**
 * The headset rule: a headset-ish name or a USB id, never a built-in device,
 * a pseudo-entry or a camera.
 */
export function isHeadset(device: CallDevice): boolean {
  if (device.kind === "videoinput" || isPseudoDevice(device)) return false;
  if (BUILT_IN_RE.test(device.label)) return false;
  return HEADSET_RE.test(device.label) || USB_ID_RE.test(device.label);
}

/** "Default - Logitech USB Headset (046d:0a44)" → "Logitech USB Headset". */
export function displayName(label: string): string {
  return label
    .replace(PSEUDO_PREFIX_RE, "")
    .replace(USB_ID_RE, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Devices a menu shows for `kind`: no pseudo-entries, no id-less entries. */
export function listedDevices(
  devices: CallDevice[],
  kind: CallDeviceKind,
): CallDevice[] {
  const seen = new Set<string>();
  return devices.filter((d) => {
    if (d.kind !== kind || !d.deviceId || isPseudoDevice(d)) return false;
    if (seen.has(d.deviceId)) return false;
    seen.add(d.deviceId);
    return true;
  });
}

/** Menu rows for `kind`: listed devices with display names. */
export function menuOptions(
  devices: CallDevice[],
  kind: CallDeviceKind,
  fallbackLabel: string,
): { deviceId: string; label: string }[] {
  return listedDevices(devices, kind).map((d, i) => ({
    deviceId: d.deviceId,
    label: displayName(d.label) || `${fallbackLabel} ${i + 1}`,
  }));
}

/** The id to hand the track for a selection (`null` → the OS default). */
export function resolveDeviceId(
  devices: CallDevice[],
  kind: CallDeviceKind,
  selected: string | null,
): string | null {
  if (selected !== null && devices.some((d) => d.kind === kind && d.deviceId === selected))
    return selected;
  if (devices.some((d) => d.kind === kind && d.deviceId === "default"))
    return "default";
  return listedDevices(devices, kind)[0]?.deviceId ?? null;
}

/** Name of the device the OS default currently points at. */
export function defaultDeviceName(
  devices: CallDevice[],
  kind: CallDeviceKind,
): string | null {
  const pseudo = devices.find((d) => d.kind === kind && d.deviceId === "default");
  const device = pseudo ?? listedDevices(devices, kind)[0];
  return device?.label ? displayName(device.label) : null;
}

/**
 * Without permission, browsers hide labels and (Safari) ids. Such a snapshot
 * can't be diffed: every device would look newly added once permission lands.
 */
function isBlindSnapshot(devices: CallDevice[]): boolean {
  return devices.some((d) => d.kind !== "videoinput" && !d.deviceId);
}

const deviceKey = (d: CallDevice) => `${d.kind}:${d.deviceId}`;

/**
 * Decide what a device-list change does to the selection.
 *
 * `prev` is the last list the caller acted on, or `null` before the first
 * enumeration — the first list is a baseline, so a headset that was already
 * connected is never treated as plugged in (a call starts on the OS default).
 * A carried-over pick that isn't in the baseline quietly becomes the default:
 * the track never got it either (LiveKit falls back when an exact id fails).
 */
export function planDeviceChange(
  prev: CallDevice[] | null,
  next: CallDevice[],
  selection: DeviceSelection,
  phase: CallDevicePhase,
): DeviceChangePlan {
  const unchanged: DeviceChangePlan = {
    selection,
    switches: [],
    notice: null,
    undo: null,
  };
  if (isBlindSnapshot(next)) return unchanged;
  // A blind list can't be diffed, so the first sighted one is the baseline.
  if (prev === null || isBlindSnapshot(prev)) {
    const kinds: CallDeviceKind[] = [...AUDIO_KINDS, "videoinput"];
    const missing = kinds.filter(
      (k) =>
        selection[k] !== null &&
        !next.some((d) => d.kind === k && d.deviceId === selection[k]),
    );
    if (missing.length === 0) return unchanged;
    const result = { ...selection };
    for (const k of missing) result[k] = null;
    return { ...unchanged, selection: result };
  }

  const prevKeys = new Set(prev.filter((d) => !isPseudoDevice(d)).map(deviceKey));
  const nextKeys = new Set(next.filter((d) => !isPseudoDevice(d)).map(deviceKey));

  const result: DeviceSelection = { ...selection };
  const switches: DeviceSwitch[] = [];
  let notice: DeviceNotice | null = null;
  let undo: DeviceSelection | null = null;
  const covered = new Set<AudioKind>();

  // Auto-switch: the first newly added headset takes over the side(s) it has.
  if (phase === "incall") {
    const addedHeadset = next.find(
      (d) => !prevKeys.has(deviceKey(d)) && isHeadset(d),
    );
    if (addedHeadset) {
      const sameDevice = (d: CallDevice) =>
        addedHeadset.groupId
          ? d.groupId === addedHeadset.groupId
          : displayName(d.label) === displayName(addedHeadset.label);
      for (const kind of AUDIO_KINDS) {
        const side = next.find(
          (d) =>
            d.kind === kind &&
            !prevKeys.has(deviceKey(d)) &&
            isHeadset(d) &&
            sameDevice(d),
        );
        if (!side) continue;
        result[kind] = side.deviceId;
        switches.push({ kind, deviceId: side.deviceId });
        covered.add(kind);
      }
      undo = { ...selection };
      notice = { kind: "auto", deviceName: displayName(addedHeadset.label) };
    }
  }

  // Fallback: an active mic or speaker that vanished goes back to the default.
  let fallback: { kind: AudioKind; oldName: string } | null = null;
  for (const kind of AUDIO_KINDS) {
    const active = selection[kind];
    if (active === null || covered.has(kind)) continue;
    if (nextKeys.has(`${kind}:${active}`)) continue;
    result[kind] = null;
    const target = resolveDeviceId(next, kind, null);
    if (target !== null) switches.push({ kind, deviceId: target });
    const lost = prev.find((d) => d.kind === kind && d.deviceId === active);
    // Name the speaker side when both fell back — it's what the user hears.
    if (!fallback || kind === "audiooutput")
      fallback = { kind, oldName: lost?.label ? displayName(lost.label) : "" };
  }
  if (fallback && phase === "incall" && !notice) {
    notice = {
      kind: "fallback",
      deviceName: defaultDeviceName(next, fallback.kind) ?? "",
      oldName: fallback.oldName,
    };
  }

  const changed = AUDIO_KINDS.some((k) => result[k] !== selection[k]);
  if (!changed && !notice) return unchanged;
  return { selection: changed ? result : selection, switches, notice, undo };
}

/** The device half of `PreJoinChoices` — what a pre-join screen carries in. */
export interface ChosenDevices {
  micDeviceId?: string;
  camDeviceId?: string;
  speakerDeviceId?: string;
}

/** A pre-join selection as choices (`null` — the OS default — is left out). */
export function chosenDevices(selection: DeviceSelection): ChosenDevices {
  return {
    micDeviceId: selection.audioinput ?? undefined,
    camDeviceId: selection.videoinput ?? undefined,
    speakerDeviceId: selection.audiooutput ?? undefined,
  };
}

/** Carried-over choices as a starting selection. */
export function selectionFromChoices(
  choices: ChosenDevices | undefined,
): DeviceSelection {
  return {
    audioinput: choices?.micDeviceId ?? null,
    audiooutput: choices?.speakerDeviceId ?? null,
    videoinput: choices?.camDeviceId ?? null,
  };
}
